import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { Button, Card } from '../../ui'
import { askFindingAi, getFindingAi, type FindingAiAnswer } from '../../lib/api'
import { formatDate } from './LiveFindings'
import { useApi } from './useApi'
import './live.css'
import { LATER_ASSESSMENT_NOTICE, NEWER_ASSESSMENT_NOTICE, REVIEW_ACKNOWLEDGEMENT_TEXT, REVIEW_ACKNOWLEDGEMENT_VERSION } from './aiReviewWording'

/**
 * Explain and Fix plan, plus separately gated recorded-evidence review, using the workspace's own Azure OpenAI.
 *
 * Draws nothing while bring-your-own AI is switched off (404) or off Monitor. Nothing is sent until a person presses a
 * button. The answer comes back with placeholders where names were removed before sending; they are put back here, in
 * the browser, from this workspace's own data. This says nothing about names inside other free text.
 *
 * Labelled as the customer's AI and not checked by Defenssive: it never changes the finding.
 */
export function AiAssist({ findingId, onShown }: { findingId: string; onShown?: (shown: boolean) => void }) {
  const [reload, setReload] = useState(0)
  const ai = useApi(() => getFindingAi(findingId), [findingId, reload], undefined, true)
  const [busy, setBusy] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [acknowledgedFinding, setAcknowledgedFinding] = useState<string | null>(null)
  const verificationAcknowledged = acknowledgedFinding === findingId
  useEffect(() => setAcknowledgedFinding(null), [findingId])

  // Tells the finding page whether this card drew anything, so its side panel offers a way here only
  // when there is somewhere to go.
  const shown = ai.state === 'ready' && ai.data.onMonitor
  useEffect(() => onShown?.(shown), [shown, onShown])

  if (ai.state !== 'ready' || !ai.data.onMonitor) return null
  const a = ai.data

  if (!a.configured) {
    return (
      <Card title="Use your AI" style={{ marginTop: 'var(--space-4)' }}>
        <p className="settings-note" style={{ marginTop: 0 }}>
          Your workspace owner can connect your organisation&rsquo;s Azure OpenAI in{' '}
          <Link to="/settings/ai">Settings, AI</Link> to explain findings and draft plans to fix them.
        </p>
      </Card>
    )
  }

  async function ask(kind: 'explain' | 'fix_plan' | 'verify_evidence') {
    setBusy(kind)
    setProblem(null)
    // An acknowledgement covers this submission, not the next request or another finding.
    if (kind === 'verify_evidence') setAcknowledgedFinding(null)
    const result = kind === 'verify_evidence'
      ? await askFindingAi(findingId, kind, verificationAcknowledged, a.verificationAcknowledgementVersion ?? undefined)
      : await askFindingAi(findingId, kind)
    setBusy(null)
    if (!result.ok) setProblem(describeAsk(result.outcome))
    setReload((n) => n + 1)
  }

  const answer = (kind: string) => a.answers.find((x) => x.kind === kind)
  const limitReached = a.usedToday !== null && a.dailyCap !== null && a.usedToday >= a.dailyCap

  return (
    <Card title="Use your AI" style={{ marginTop: 'var(--space-4)' }}>
      <p className="settings-note" style={{ marginTop: 0 }}>
        Explain and Fix plan send this finding to your organisation&rsquo;s Azure OpenAI. Recognised names,
        addresses and identifiers in the evidence are replaced first; names inside other free text may remain.
        {a.dailyCap !== null ? ` ${a.usedToday ?? 0} of ${a.dailyCap} requests used today.` : ''}
      </p>
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginTop: 'var(--space-3)' }}>
        <Button variant="primary" disabled={busy !== null || limitReached} onClick={() => void ask('explain')}>
          {busy === 'explain' ? 'Asking…' : answer('explain') ? 'Explain again' : 'Explain this finding'}
        </Button>
        <Button disabled={busy !== null || limitReached} onClick={() => void ask('fix_plan')}>
          {busy === 'fix_plan' ? 'Asking…' : answer('fix_plan') ? 'Draft the fix plan again' : 'Draft a fix plan'}
        </Button>
      </div>
      {limitReached ? <p className="settings-note">Today&rsquo;s limit is reached. It resets at midnight UTC.</p> : null}
      {a.verificationEnabled && a.canReview
        && a.verificationAcknowledgementVersion === REVIEW_ACKNOWLEDGEMENT_VERSION
        && a.verificationAcknowledgementText === REVIEW_ACKNOWLEDGEMENT_TEXT ? <section aria-label="Recorded evidence review">
        <h3 className="finding-references-heading">Review recorded evidence</h3>
        <p className="settings-note">Owner-only, on demand. Your Azure OpenAI reviews whether the recorded facts
          support this finding. Only recognised permissions, supported setting values and counts may be disclosed
          as evidence. Other evidence values are withheld. This reads no fresh tenant data, does not test current
          enforcement, and cannot change or close the finding. The answer can be wrong.</p>
        <label><input type="checkbox" checked={verificationAcknowledged}
          disabled={busy !== null}
          onChange={(e) => setAcknowledgedFinding(e.target.checked ? findingId : null)} /> {a.verificationAcknowledgementText}</label>
        <p className="settings-hint"><a href="/privacy">What is sent and kept</a></p>
        <div style={{ marginTop: 'var(--space-3)' }}><Button
          disabled={!verificationAcknowledged || busy !== null || limitReached}
          onClick={() => void ask('verify_evidence')}>
          {busy === 'verify_evidence' ? 'Reviewing…' : 'Review recorded evidence'}</Button></div>
        {answer('verify_evidence') ? <VerificationAnswer answer={answer('verify_evidence')!} /> : null}
      </section> : null}
      {problem ? <p className="live-error" role="alert" style={{ marginTop: 'var(--space-3)' }}>{problem}</p> : null}

      {(['explain', 'fix_plan'] as const).map((kind) => {
        const found = answer(kind)
        return found ? <Answer key={kind} answer={found} /> : null
      })}
    </Card>
  )
}

function VerificationAnswer({ answer }: { answer: FindingAiAnswer }) {
  type Review = { verdict: string; reason: string; evidenceIds: string[]; missingEvidence: string[];
    evidenceAsOf: string; inputFingerprint: string; isStale: boolean; historyReason?: string; laterAssessmentDidNotFinish?: boolean;
    acknowledgementId?: string; acknowledgementVersion?: string }
  let review: Review
  try { review = JSON.parse(answer.text) as Review } catch { return <p role="alert">The saved review could not be read.</p> }
  const label = review?.verdict === 'supported' ? 'Supported by the supplied evidence'
    : review?.verdict === 'unsupported' ? 'Contradicted by the supplied evidence'
    : review?.verdict === 'insufficient_evidence' ? 'Insufficient evidence' : null
  if (!label || typeof review.reason !== 'string' || !Array.isArray(review.evidenceIds)
    || !review.evidenceIds.every((id) => typeof id === 'string')
    || !Array.isArray(review.missingEvidence) || !review.missingEvidence.every((item) => typeof item === 'string')
    || typeof review.evidenceAsOf !== 'string' || typeof review.inputFingerprint !== 'string'
    || (review.verdict !== 'insufficient_evidence' && review.missingEvidence.length !== 0))
    return <p role="alert">The saved review could not be read.</p>
  return <section className="ai-answer" aria-label="Recorded evidence review result">
    <h3 className="finding-references-heading">{label}</h3>
    {review.isStale ? <p role="alert">Historical review: {review.historyReason === 'newer_assessment'
      ? NEWER_ASSESSMENT_NOTICE
      : 'the finding or evidence has changed. Run a new review.'}</p> : null}
    {review.laterAssessmentDidNotFinish ? <p role="alert">{LATER_ASSESSMENT_NOTICE}</p> : null}
    <p>{present(review.reason, answer.placeholders)}</p>
    {review.evidenceIds.length ? <p>Cited evidence rows: {review.evidenceIds.join(', ')} (E1 is the first recorded row).</p> : null}
    {review.missingEvidence.length ? <><h4>Not established</h4><ul>{review.missingEvidence.map((missing, i) =>
      <li key={i}>{present(missing, answer.placeholders)}</li>)}</ul></> : null}
    <p className="settings-hint">Evidence as of {formatDate(review.evidenceAsOf)}.
      {answer.model
        ? ` Reviewed by your Azure OpenAI (${answer.model}). AI review can be wrong. This is recorded evidence, not live verification; it never changes or closes the finding.`
        : ' Model was not called. No finding was sent to Azure OpenAI for this review, and no model-request allowance was used. This result does not establish that the tenant is safe.'}</p>
    <details><summary>Input fingerprint</summary><code>{review.inputFingerprint}</code></details>
    {typeof review.acknowledgementId === 'string' && typeof review.acknowledgementVersion === 'string'
      ? <p className="settings-hint">Acknowledgement receipt: <code>{review.acknowledgementId}</code> ({review.acknowledgementVersion}).</p>
      : null}
  </section>
}

function Answer({ answer }: { answer: FindingAiAnswer }) {
  return (
    <section className="ai-answer" aria-label={answer.kind === 'fix_plan' ? 'Fix plan' : 'Explanation'}>
      <h3 className="finding-references-heading">{answer.kind === 'fix_plan' ? 'Fix plan' : 'Explanation'}</h3>
      <div className="ai-answer-text">{present(answer.text, answer.placeholders)}</div>
      <p className="settings-hint">
        Written by your Azure OpenAI{answer.model ? ` (${answer.model})` : ''} on {formatDate(answer.createdAt)}. Not
        checked by Defenssive. Recognised names were replaced before sending; placeholders are filled in here
        from your own data. Names inside other free text may remain.
      </p>
    </section>
  )
}

/**
 * The answer as the page shows it. Plain text, never HTML: any Markdown the model still wrote (bold, # headings,
 * horizontal rules) is tidied away, and each placeholder becomes the real name, marked so a reader can see it was filled
 * in here from the workspace's own data and was not sent to the model.
 */
export function present(text: string, placeholders: Record<string, string>): ReactNode[] {
  const keys = Object.keys(placeholders).sort((x, y) => y.length - x.length)
  const lines = tidy(text).split('\n')
  return lines.map((line, i) => (
    <span key={i}>
      {fill(line, keys, placeholders)}
      {i < lines.length - 1 ? '\n' : null}
    </span>
  ))
}

/**
 * Markdown left in a model's answer, removed: bold markers, heading hashes, rules, code fences and the backticks
 * around a command (they showed as `Get-MgServicePrincipal…`), and runs of blank lines.
 */
export function tidy(text: string): string {
  return text
    .split('\n')
    .filter((line) => !/^\s*([-*_])\1{2,}\s*$/.test(line) && !/^\s*```/.test(line))
    .map((line) =>
      line
        .replace(/^\s*#{1,6}\s+/, '')
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/__(.+?)__/g, '$1')
        .replace(/`([^`]+)`/g, '$1'),
    )
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

const escapeForPattern = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

function fill(line: string, keys: string[], placeholders: Record<string, string>): ReactNode[] {
  if (keys.length === 0) return [line]
  const pattern = new RegExp(`(${keys.map(escapeForPattern).join('|')})`)
  return line.split(pattern).map((part, i) =>
    placeholders[part] !== undefined ? (
      <mark key={i} className="ai-restored" title="Filled in here from your workspace's own data. Not sent to your AI.">
        {placeholders[part]}
      </mark>
    ) : (
      part
    ),
  )
}

/** Puts the real names back, here in the browser. Longest placeholders first, so "[group 10]" is not read as "[group 1]". */
export function restore(text: string, placeholders: Record<string, string>): string {
  return Object.keys(placeholders)
    .sort((x, y) => y.length - x.length)
    .reduce((out, key) => out.split(key).join(placeholders[key]), text)
}

/** Why a request did not answer, in words. */
export function describeAsk(outcome: string | undefined): string {
  switch (outcome) {
    case 'owner_required': return 'Only a workspace owner can request an evidence review.'
    case 'confirmation_required': return 'Confirm evidence sharing before requesting a review.'
    case 'confirmation_changed': return 'The review acknowledgement has changed. Reload and acknowledge the current wording.'
    case 'evidence_changed': return 'The finding or AI configuration changed during review. Run it again.'
    case 'not_enabled': return 'Recorded-evidence review is not enabled.'
    case 'over_cap':
      return 'Today’s limit is reached. Your workspace owner sets it in Settings, AI.'
    case 'busy':
      return 'You already have a request running. Wait for it to finish.'
    case 'forbidden':
      return 'Your Azure OpenAI refused Defenssive. Your owner can check the role in Settings, AI.'
    case 'not_found':
      return 'Your Azure OpenAI has no such deployment. Your owner can check it in Settings, AI.'
    case 'throttled':
      return 'Your Azure OpenAI is busy or out of quota. Try again later.'
    case 'unreachable':
      return 'Your Azure OpenAI did not answer. Try again later.'
    case 'rejected':
      return 'Your Azure OpenAI declined this request, possibly because of its content filter.'
    case 'not_configured':
    case 'not_on_monitor':
      return 'AI is not set up for this workspace.'
    default:
      return 'Something went wrong. Try again.'
  }
}
